import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtAED, fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { CalendarCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const today = () => new Date().toISOString().slice(0, 10);

export default function DayClose() {
  const { currentOutlet } = useAuth();
  const [date, setDate] = useState(today());
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [viewRec, setViewRec] = useState(null);

  const loadSummary = useCallback(async () => {
    const params = { date };
    if (currentOutlet) params.outlet_id = currentOutlet.id;
    const { data } = await api.get('/day-close/summary', { params });
    setSummary(data);
  }, [date, currentOutlet]);
  const loadHistory = async () => { const { data } = await api.get('/day-close'); setHistory(data); };

  const closedToday = history.find((r) => r.date === date && (!currentOutlet || !r.outlet_id || r.outlet_id === currentOutlet.id));

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadHistory(); }, []);

  const doClose = async () => {
    try {
      await api.post('/day-close', { outlet_id: currentOutlet?.id, date, counted_cash: counted === '' ? null : Number(counted), note });
      setOpen(false); setCounted(''); setNote('');
      loadHistory(); toast.success(`Day ${date} closed & saved`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Close failed'); }
  };

  const Row = ({ label, value, bold }) => (
    <div className={`flex justify-between py-1.5 ${bold ? 'font-bold text-base border-t mt-1 pt-2' : 'text-sm'}`}><span className={bold ? '' : 'text-muted-foreground'}>{label}</span><span className="font-mono">{value}</span></div>
  );

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-display text-2xl font-bold">Day Close</h1><p className="text-sm text-muted-foreground">Review & finalize the day's sales, expenses and payments</p></div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Business date</Label><Input type="date" data-testid="dayclose-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" /></div>
          <Button data-testid="dayclose-open-button" onClick={() => setOpen(true)}><Lock className="h-4 w-4 mr-1" /> Close Day</Button>
        </div>
      </div>
      <PageHelp id="dayclose">See a live summary of the selected day — total sales, sales by payment mode, expenses and net cash. Press <b>Close Day</b> to save a permanent dated record for your books.</PageHelp>

      {summary && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-5">
            <div className="font-display font-semibold mb-2">Sales & Collections — {summary.date}</div>
            <Row label="Orders" value={summary.order_count} />
            <Row label="Gross Sales" value={fmtAED(summary.total_sales)} />
            <Row label="VAT collected (5%)" value={fmtAED(summary.vat_total)} />
            <div className="text-xs font-semibold text-muted-foreground mt-3 mb-1">Payment collections</div>
            {Object.keys(summary.sales_by_mode).length === 0 && <div className="text-sm text-muted-foreground">No completed sales</div>}
            {Object.entries(summary.sales_by_mode).map(([k, v]) => <Row key={k} label={<span className="capitalize">{k}</span>} value={fmtAED(v)} />)}
          </Card>
          <Card className="p-5">
            <div className="font-display font-semibold mb-2">Expenses & Closing</div>
            <Row label="Total Expenses" value={fmtAED(summary.total_expenses)} />
            {Object.entries(summary.expenses_by_mode).map(([k, v]) => <Row key={k} label={<span className="capitalize pl-3">· {k}</span>} value={fmtAED(v)} />)}
            <Row label="Expected Closing Cash (cash sales − cash expenses)" value={fmtAED(summary.net_cash)} bold />
            <Row label="Net Revenue (sales − expenses)" value={fmtAED(summary.net_revenue)} />
            {closedToday && <Row label="Closing Balance (final)" value={fmtAED(closedToday.closing_balance)} bold />}
          </Card>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="p-4 font-display font-semibold flex items-center gap-2"><CalendarCheck className="h-4 w-4" /> Closed Days</div>
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Sales</TableHead><TableHead className="text-right">Expenses</TableHead><TableHead className="text-right">Closing Balance</TableHead><TableHead>Closed By</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {history.map((r) => (
              <TableRow key={r.id} data-testid={`dayclose-row-${r.date}`}>
                <TableCell className="font-medium">{r.date}</TableCell>
                <TableCell className="text-right font-mono">{r.order_count}</TableCell>
                <TableCell className="text-right font-mono">{fmtAED(r.total_sales)}</TableCell>
                <TableCell className="text-right font-mono">{fmtAED(r.total_expenses)}</TableCell>
                <TableCell className="text-right font-mono">{fmtAED(r.closing_balance ?? r.net_cash)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.closed_by_name}<br />{fmtDate(r.created_at)}</TableCell>
                <TableCell><Button size="sm" variant="outline" className="h-8" data-testid={`dayclose-view-${r.date}`} onClick={() => setViewRec(r)}>View</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {history.length === 0 && <div className="p-8 text-center text-muted-foreground">No day-close records yet</div>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Close Day — {date}</DialogTitle></DialogHeader>
          {summary && (
            <div className="space-y-2 text-sm">
              <Row label="Gross Sales" value={fmtAED(summary.total_sales)} />
              <Row label="Total Expenses" value={fmtAED(summary.total_expenses)} />
              <Row label="Expected Cash in Drawer" value={fmtAED(summary.net_cash)} bold />
              <div className="pt-1"><Label>Counted cash (optional)</Label><Input type="number" data-testid="dayclose-counted" placeholder="Physically counted cash" value={counted} onChange={(e) => setCounted(e.target.value)} /></div>
              {counted !== '' && <div className="text-sm">Difference: <b className={Number(counted) - summary.net_cash === 0 ? 'text-success' : 'text-destructive'}>{fmtAED(Number(counted) - summary.net_cash)}</b></div>}
              <div><Label>Note</Label><Input data-testid="dayclose-note" placeholder="Optional" value={note} onChange={(e) => setNote(e.target.value)} /></div>
              <Button className="w-full" data-testid="dayclose-confirm" onClick={doClose}><Lock className="h-4 w-4 mr-1" /> Confirm & Save Day Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewRec} onOpenChange={(o) => !o && setViewRec(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Day Close — {viewRec?.date}</DialogTitle></DialogHeader>
          {viewRec && (
            <div className="text-sm">
              <Row label="Orders" value={viewRec.order_count} />
              <Row label="Gross Sales" value={fmtAED(viewRec.total_sales)} />
              <Row label="VAT collected" value={fmtAED(viewRec.vat_total)} />
              {Object.entries(viewRec.sales_by_mode || {}).map(([k, v]) => <Row key={k} label={<span className="capitalize pl-3">· {k}</span>} value={fmtAED(v)} />)}
              <Row label="Total Expenses" value={fmtAED(viewRec.total_expenses)} />
              <Row label="Net Cash" value={fmtAED(viewRec.net_cash)} bold />
              {viewRec.counted_cash != null && <Row label="Counted Cash" value={fmtAED(viewRec.counted_cash)} />}
              {viewRec.note && <div className="text-muted-foreground pt-2">Note: {viewRec.note}</div>}
              <div className="text-xs text-muted-foreground pt-2">Closed by {viewRec.closed_by_name} · {fmtDate(viewRec.created_at)}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
